export type Tag = 'definitif' | 'projete';
export type Scenario = 'tous' | 'definitif';

export interface Changement {
  id: number;
  tag: Tag;
  commentaire: string;
}

export interface RepartitionCellule {
  evenementId: number;
  tag: Tag;
  lignes: Record<number, number>; // categorieId -> points d'ETP
  somme: number;
  ecart: number;
}

export interface CelluleMois {
  mois: number;
  nominal: number;
  taux: number;
  montant: number;
  treizieme: number;
  tagNominal: Tag | null;
  tagTaux: Tag | null;
  changementNominal: Changement | null;
  changementTaux: Changement | null;
  repartition: RepartitionCellule | null;
}

export interface LigneEmploye {
  id: number;
  nom: string;
  categorie: string;
  debutActivite: string | null;
  finActivite: string | null;
  note: string | null;
  mois: CelluleMois[];
  totalAnnuel: number;
  aProjections: boolean;
  aRepartition: boolean;
}

export interface Grille {
  annee: number;
  scenario: Scenario;
  employes: LigneEmploye[];
  totauxMensuels: number[];
  totalAnnuel: number;
  tauxChargesSociales: number;
}

export interface Categorie {
  id: number;
  nom: string;
  parentId: number | null;
  ordre: number;
}

export interface EvenementSalaire {
  id: number;
  employe_id: number;
  mois_effet: string;
  montant: string;
  tag: Tag;
  commentaire: string;
  cree_le: string;
}

export interface EvenementTaux {
  id: number;
  employe_id: number;
  mois_effet: string;
  taux: string;
  tag: Tag;
  commentaire: string;
  cree_le: string;
}

export interface EvenementRepartition {
  id: number;
  employe_id: number;
  mois_effet: string;
  tag: Tag;
  commentaire: string;
  cree_le: string;
  lignes: Array<{ id: number; categorieId: number; taux: string }>;
}

export interface Employe {
  id: number;
  nom: string;
  categorie: string;
  debut_activite: string | null;
  fin_activite: string | null;
  note: string | null;
}

export interface EmployeDetail {
  employe: Employe;
  salaireEvenements: EvenementSalaire[];
  tauxEvenements: EvenementTaux[];
  repartitionEvenements: EvenementRepartition[];
}

export const CATEGORIES_CONTRACTUELLES = [
  'Direction',
  'Encadrement',
  'Administration',
  'Stagiaires',
  'Auxiliaires',
  'Autres',
] as const;

// ── Module Revenus ──────────────────────────────────────────────────────────

// 'consolide' = passé vérifié contre la banque (écrit par le futur sous-module
// de consolidation ; l'UI le lit mais ne le crée pas).
export type TagRevenu = 'definitif' | 'projete' | 'consolide';
export type ModeFlux = 'montant' | 'quantite_tarif' | 'libre' | 'convention';

export interface ScenarioBudget {
  id: number;
  nom: string;
  description: string | null;
  archive: boolean;
  // socle : LE budget de référence (décisions + hypothèses retenues), lecture
  // par défaut de toutes les vues et parent naturel des variantes.
  socle?: boolean;
  parent_id?: number | null;
  nb_evenements?: number;
}

export interface ChangementRevenu {
  id: number;
  tag: TagRevenu;
  commentaire: string;
}

export interface RecetteLibreCellule {
  id: number;
  libelle: string;
  montant: number;
  tag: TagRevenu;
  commentaire: string;
}

export interface CelluleFlux {
  mois: number;
  montant: number;
  quantite: number | null;
  tarif: number | null;
  tagMontant: TagRevenu | null;
  tagQuantite: TagRevenu | null;
  tagTarif: TagRevenu | null;
  changementMontant: ChangementRevenu | null;
  changementQuantite: ChangementRevenu | null;
  changementTarif: ChangementRevenu | null;
  recettes: RecetteLibreCellule[] | null;
  derive: boolean; // mode convention : montant dérivé du contrat, non éditable
  tarifDerive: boolean; // mode quantite_tarif : tarif dérivé de la prestation
  tarifHoraire: boolean; // tarif à l'heure : le montant ne se dérive pas de la quantité
  montantReel: boolean; // mode quantite_tarif : montant facturé réel remplaçant quantité × tarif
  source: string | null; // provenance d'une valeur dérivée (infobulle)
  // mois_effet de l'événement en vigueur : antérieur au mois de la cellule =
  // valeur reportée (un mois futur reporté s'affiche « prévision »)
  moisEffetMontant: string | null;
  moisEffetQuantite: string | null;
}

export interface LigneFlux {
  id: number;
  cle: string;
  libelle: string;
  groupe: string;
  mode: ModeFlux;
  programme: string | null;
  conventionne: boolean;
  clientId: number | null;
  clientNom: string | null;
  contratId: number | null;
  contratLibelle: string | null;
  prestationId: number | null;
  prestationLibelle: string | null;
  uniteTarif: 'mois' | 'heure' | null;
  positionTarifaire: string | null;
  moisFacturation: number[] | null;
  lagEncaissement: number;
  actif: boolean;
  note: string | null;
  mois: CelluleFlux[];
  totalAnnuel: number;
  aProjections: boolean;
}

export interface SousTotalGroupe {
  groupe: string;
  parMois: number[];
  annuel: number;
}

export interface RetrocessionProgramme {
  contratId: number;
  contratLibelle: string;
  programme: string;
  annee: number;
  prix: number; // annuel par place
  prixMensuel: number;
  places: number;
  plafond: number;
  tagTerme: TagRevenu;
  moisPlacesConvention: number; // places × 12
  moisPlacesRemplies: number; // réels + projections + défaut (places) pour les mois sans décompte
  manqueMoisPlaces: number;
  manque: number; // CHF
  factureConventionne: number;
  factureIndividualise: number;
  risque: number; // min(individualisé, manque)
  surUtilisation: number; // mois-places au-delà de la convention
  moisFactures: number;
  moisPlacesFacturees: number;
  moisPlacesRestantes: number;
  rythmeActuel: number;
  rythmeMaxRestant: number;
  imputationDecembre: number; // rétrocession déduite de la prévision de décembre (grille Revenus)
  tagOccupation: TagRevenu | null; // null = aucun décompte réel (prévision entièrement au défaut)
}

export interface GrilleRevenus {
  annee: number;
  scenario: 'definitif' | number;
  flux: LigneFlux[];
  sousTotaux: SousTotalGroupe[];
  totauxMensuels: number[];
  totalAnnuel: number;
  retrocessions: RetrocessionProgramme[];
  scenarios: ScenarioBudget[];
}

export interface EvenementRevenu {
  id: number;
  flux_id: number;
  mois_effet: string;
  montant?: string;
  tarif?: string;
  quantite?: string;
  tag: TagRevenu;
  scenario_id: number | null;
  commentaire: string;
  cree_le: string;
}

export interface RecetteLibre {
  id: number;
  flux_id: number;
  mois: string;
  libelle: string;
  montant: string;
  tag: TagRevenu;
  scenario_id: number | null;
  commentaire: string;
  cree_le: string;
}

// ── Référentiel Prestations ─────────────────────────────────────────────────

// Unité stable de l'analyse : les paniers des contrats changent, la
// prestation non. Voir schema.sql (table prestation).
export interface Prestation {
  id: number;
  cle: string;
  libelle: string;
  groupe: string | null;
  description: string | null;
  ordre: number;
  actif: boolean;
  cree_le: string;
}

// ── Modules Clients / Contrats / Mesures ────────────────────────────────────

export interface Client {
  id: number;
  cle: string;
  nom: string;
  acronyme: string | null;
  categorie: string;
  parent_id: number | null;
  adresse: string | null;
  contact_nom: string | null;
  contact_email: string | null;
  contact_telephone: string | null;
  note: string | null;
  actif: boolean;
  cree_le: string;
}

export interface ConventionTerme {
  id: number;
  annee: number;
  prix: string;
  places: string;
  tag: TagRevenu;
  scenario_id: number | null;
  commentaire: string;
  cree_le: string;
}

export interface TarifPrestationEvenement {
  id: number;
  mois_effet: string;
  tarif: string;
  tag: TagRevenu;
  scenario_id: number | null;
  commentaire: string;
  cree_le: string;
}

// Panier de prestations d'une ligne de contrat, historié : à partir de
// mois_effet la prestation entre (inclus) ou sort du panier. Ce que recouvre
// une « mesure » change au fil des ans ; les prestations, elles, ne changent pas.
export interface ContenuPanier {
  id: number;
  prestation_id: number;
  prestation_libelle: string;
  prestation_groupe: string | null;
  mois_effet: string;
  inclus: boolean;
  commentaire: string;
  cree_le: string;
}

export interface ContratPrestation {
  id: number;
  cle: string;
  libelle: string;
  // Unité du tarif : forfait mensuel par assuré, ou temps passé (coaching OAI,
  // orientation art. 15 — facturés à l'heure, au quart d'heure entamé).
  unite: 'mois' | 'heure';
  positionTarifaire: string | null; // code du catalogue du mandant (905.041.2.1…)
  ordre: number;
  actif: boolean;
  tarifs: TarifPrestationEvenement[];
  contenu: ContenuPanier[];
}

export interface Contrat {
  id: number;
  cle: string;
  categorie: 'convention_mesures' | 'convention_cadre';
  libelle: string;
  programme: string | null;
  note: string | null;
  actif: boolean;
  cree_le: string;
  client_id: number;
  client_nom: string;
  client_acronyme: string | null;
  termes: ConventionTerme[];
  prestations: ContratPrestation[];
}

export interface MesureEvenement {
  id: number;
  programme: string;
  type: string;
  mois_effet: string;
  nombre: string;
  tag: TagRevenu;
  scenario_id: number | null;
  commentaire: string;
  cree_le: string;
}

export interface CelluleMesure {
  mois: number;
  nombre: number | null;
  tag: TagRevenu | null; // null quand la valeur est le défaut (places conventionnées)
  parDefaut: boolean; // prévision par défaut : places du terme, aucun décompte saisi
  changement: ChangementRevenu | null;
}

export interface SerieMesures {
  programme: string;
  type: string;
  mois: CelluleMesure[];
  evenements: Array<{
    id: number;
    mois_effet: string;
    nombre: number;
    tag: TagRevenu;
    scenario_id: number | null;
    commentaire: string;
  }>;
  aProjections: boolean;
}

export interface ReferenceProgramme {
  programme: string;
  contratId: number;
  contratLibelle: string;
  places: number | null;
  tagTerme: TagRevenu | null;
}

export interface GrilleMesures {
  annee: number;
  scenario: 'definitif' | number;
  series: SerieMesures[];
  references: ReferenceProgramme[];
  scenarios: ScenarioBudget[];
}

// ── Loyers ────────────────────────────────────────────────────────────────

export interface CelluleLoyer {
  mois: number;
  loyer: number;
  charges: number;
  total: number;
  tagLoyer: TagRevenu | null;
  tagCharges: TagRevenu | null;
  moisEffetLoyer: string | null;
  moisEffetCharges: string | null;
  changementLoyer: ChangementRevenu | null;
  changementCharges: ChangementRevenu | null;
}

export interface EvenementLoyer {
  id: number;
  mois_effet: string;
  montant: number;
  tag: TagRevenu;
  scenario_id: number | null;
  commentaire: string;
}

export interface LigneSite {
  id: number;
  nom: string;
  adresse: string | null;
  m2: number | null;
  note: string | null;
  groupe: string | null; // catégorie de regroupement (généraux, CCC, antennes…)
  actif: boolean;
  mois: CelluleLoyer[];
  totalLoyer: number;
  totalCharges: number;
  totalAnnuel: number;
  chfM2An: number | null; // loyer annuel ÷ m²
  aProjections: boolean;
  evenementsLoyer: EvenementLoyer[];
  evenementsCharges: EvenementLoyer[];
}

export interface GrilleLoyers {
  annee: number;
  scenario: 'definitif' | number;
  sites: LigneSite[];
  totauxMensuels: { loyer: number[]; charges: number[]; total: number[] };
  totalAnnuel: { loyer: number; charges: number; total: number };
  scenarios: ScenarioBudget[];
}

export interface ReferenceFlux {
  client: { id: number; nom: string; acronyme: string | null; chemin: string } | null;
  contrat: {
    id: number;
    cle: string;
    libelle: string;
    categorie: string;
    programme: string | null;
    note: string | null;
    termes: ConventionTerme[];
  } | null;
  prestation: {
    id: number;
    cle: string;
    libelle: string;
    contrat_id: number;
    contrat_libelle: string;
    tarifs: TarifPrestationEvenement[];
  } | null;
}

export interface FluxDetail {
  flux: {
    id: number;
    cle: string;
    libelle: string;
    groupe: string;
    mode: ModeFlux;
    programme: string | null;
    client_id: number | null;
    contrat_id: number | null;
    prestation_id: number | null;
    mois_facturation: number[] | null;
    lag_encaissement: number;
    actif: boolean;
    note: string | null;
  };
  montantEvenements: EvenementRevenu[];
  quantiteEvenements: EvenementRevenu[];
  recettes: RecetteLibre[];
  reference: ReferenceFlux;
}

export const TYPES_MESURE_LABELS: Record<string, string> = {
  conventionnee: 'Conventionnées',
  individualisee: 'Individualisées',
};

export const GROUPES_FLUX_LABELS: Record<string, string> = {
  DIRIS: 'DIRIS / DGCS',
  OAI: 'OAI',
  SISP: 'SISP',
  EVAM: 'EVAM',
  AUTRES: 'Autres recettes',
};

export const MOIS_LABELS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc'];
export const MOIS_LABELS_LONGS = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];
